/**
 * Shared "click-once" guard for HITL resume buttons in `deep_web_search`
 * runway panels.
 *
 * Problem this fixes (observed across CrossQueryPlanReviewPanel,
 * DeepWebSearchDomainsReviewPanel, DeepWebSearchConfigReviewPanel):
 *
 *   1. The user clicks "Approve & Continue" / "Continue with edited plan".
 *   2. The hook calls `await resume(...)` which dispatches a `/resume` POST
 *      to Rails.
 *   3. The component sets `setBusy(true)` to disable the button — but React
 *      schedules that update; if the user clicks again BEFORE the next paint
 *      (or the `useDeepWebSearchHitl` `busy` flag updates), we fire a SECOND
 *      `/resume` POST. The Python executor logs show this as overlapping
 *      `Resume request` entries and duplicated `node_start` events.
 *
 * What this hook gives you:
 *   - `submitted`     — boolean state, true the moment any wrapped action
 *                       fires. Use this to disable / hide the action row.
 *   - `submit(fn)`    — wrap your async resume call. The first invocation
 *                       flips `submitted` synchronously via `flushSync`
 *                       (so the next click cannot win the race) and runs
 *                       `fn`. Re-entrant calls before `fn` resolves are
 *                       ignored. On thrown errors the flag is reset so the
 *                       user can retry.
 *   - `reset()`       — clear the flag manually if you need to (e.g. after
 *                       client-side validation rejects the submission and
 *                       you want to keep the row clickable).
 *
 * Sticky semantics: once `submit` succeeds, `submitted` stays true. The
 * caller is expected to remount the panel (new interrupt key) which will
 * naturally clear local state. If the panel is reused across interrupts,
 * pass an `interruptKey` and the hook resets `submitted` whenever it
 * changes.
 */
import { useCallback, useEffect, useRef, useState } from "react"
import { flushSync } from "react-dom"

export default function useHitlSubmitOnce({ interruptKey = null } = {}) {
  const [submitted, setSubmitted] = useState(false)
  const inFlightRef = useRef(false)
  const lastKeyRef = useRef(interruptKey)

  // Reset whenever the active HITL gate changes (run id, step, etc.).
  // Without this, a panel that survives across interrupts (rare, but
  // possible when the same component handles multiple steps) would stay
  // permanently locked after the first resume.
  useEffect(() => {
    if (interruptKey !== lastKeyRef.current) {
      lastKeyRef.current = interruptKey
      inFlightRef.current = false
      setSubmitted(false)
    }
  }, [interruptKey])

  const reset = useCallback(() => {
    inFlightRef.current = false
    setSubmitted(false)
  }, [])

  const submit = useCallback(async (fn) => {
    if (inFlightRef.current) return undefined
    inFlightRef.current = true
    flushSync(() => {
      setSubmitted(true)
    })
    try {
      return await fn()
    } catch (e) {
      inFlightRef.current = false
      setSubmitted(false)
      throw e
    }
  }, [])

  return { submitted, submit, reset }
}
